import React from 'react';
import { Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'i18n/TranslationProvider';

/**
 * Renders a Google Maps Static API image — no JS SDK, no dynamic tiles.
 * ~$2/1000 requests vs ~$7/1000 for the Maps JavaScript API.
 *
 * Drop-in replacement for the interactive MapContainer. Accepts the same
 * origin/destination/center/zoom props; ignores JS-only props (fitBoundsPadding,
 * maxAutoZoom, mapContainerStyle) for compatibility.
 */

function buildUrl({ origin, destination, center, zoom = 6, width = 640, height = 320 }) {
  const key = process.env.REACT_APP_GOOGLE_API_KEY;
  if (!key) return null;

  // Build as array to avoid URLSearchParams encoding the pipe `|` separators
  // that the Static API uses inside marker/path values.
  const parts = [
    `size=${width}x${height}`,
    'scale=2',       // retina — doubles pixel density, same billing unit
    'maptype=roadmap',
    `key=${key}`,
  ];

  if (origin) {
    parts.push(`markers=color:0x4285F4|label:A|${origin.lat},${origin.lng}`);
  }
  if (destination) {
    parts.push(`markers=color:0xEA4335|label:B|${destination.lat},${destination.lng}`);
  }

  if (origin && destination) {
    const latDiff = Math.abs(origin.lat - destination.lat);
    const lngDiff = Math.abs(origin.lng - destination.lng);

    // Minimum span of ~15 km per axis prevents near-zero diffs (e.g. a purely
    // east-west route where latDiff ≈ 0) from zooming in so far that the marker
    // icons get clipped at the top/bottom edge of the image.
    const MIN_SPAN = 0.14; // ≈ 15 km
    const effectiveLat = Math.max(latDiff, MIN_SPAN);
    const effectiveLng = Math.max(lngDiff, MIN_SPAN);

    // 45% outward padding on every side keeps both marker icons fully inside
    // the frame at any distance (10 km same-city through 2 000 km cross-country).
    const PAD = 0.45;
    const minLat = Math.min(origin.lat, destination.lat) - effectiveLat * PAD;
    const maxLat = Math.max(origin.lat, destination.lat) + effectiveLat * PAD;
    const minLng = Math.min(origin.lng, destination.lng) - effectiveLng * PAD;
    const maxLng = Math.max(origin.lng, destination.lng) + effectiveLng * PAD;

    // `visible` tells the Static API to choose the highest zoom level that fits
    // all listed lat/lng corner points — both A and B pins are always visible.
    parts.push(`visible=${minLat},${minLng}|${maxLat},${maxLng}`);
  } else {
    const point = origin || destination;
    if (point) {
      parts.push(`center=${point.lat},${point.lng}`);
      parts.push('zoom=9');
    } else if (center) {
      parts.push(`center=${center.lat},${center.lng}`);
      parts.push(`zoom=${zoom}`);
    } else {
      parts.push('center=50.5,10.5');
      parts.push(`zoom=${zoom}`);
    }
  }

  return `https://maps.googleapis.com/maps/api/staticmap?${parts.join('&')}`;
}

export default function StaticMap({
  origin,
  destination,
  center,
  zoom = 6,
  width = 800,
  height = 400,
  emptyMessage = 'The map will appear when the pickup and delivery addresses are added.',
  // Accept but ignore interactive-map-only props so callers need no changes
  mapContainerStyle: _mapContainerStyle,
  fitBoundsPadding: _fitBoundsPadding,
  maxAutoZoom: _maxAutoZoom,
}) {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const hasCoords = origin || destination || center;
  const url = hasCoords ? buildUrl({ origin, destination, center, zoom, width, height }) : null;
  const {t} = useTranslation();
  if (!url) {
    return (
      <Flex h="100%" align="center" justify="center" px={6} textAlign="center">
        <Text fontSize="sm" color={mutedColor}>
          {t(emptyMessage)}
        </Text>
      </Flex>
    );
  }

  return (
    <img
      src={url}
      alt="Route map"
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
      loading="lazy"
    />
  );
}
